import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import heroImage from '../assets/hero-image.svg';
import RecentResourcesSection from '../components/resources/RecentResourcesSection';

/**
 * HomePage component - Landing page for the application
 */
const HomePage = () => {
  const { isAuthenticated } = useAuth();
  
  return (
    <div className="bg-gray-50">
      {/* Hero section */}
      <section className="bg-primary-700 text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4">
              The Ecosystem for Biotech & Health Tech Innovation
              </h1>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold leading-tight mb-4">
              Connect, Collaborate, and Accelerate Your Innovations
              </h2>
              <p className="text-lg md:text-xl mb-8">
              Biotech01 is your all-in-one hub designed exclusively for biotech and health tech startups, investors, pharmaceutical companies, and specialized service providers. Our platform simplifies connections, fosters strategic partnerships, and accelerates innovation—helping transform groundbreaking ideas into impactful healthcare solutions.
              </p>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                {isAuthenticated ? (
                  <Link 
                    to="/dashboard" 
                    className="btn-primary text-center py-3 px-6 rounded-lg text-lg"
                  >
                    Go to Dashboard
                  </Link>
                ) : (
                  <>
                    <Link 
                      to="/signup" 
                      className="btn-primary text-center py-3 px-6 rounded-lg text-lg"
                    >
                      Get Started
                    </Link>
                    <Link 
                      to="/signin" 
                      className="bg-white text-primary-700 hover:bg-gray-100 text-center py-3 px-6 rounded-lg text-lg"
                    >
                      Sign In
                    </Link>
                  </>
                )}
              </div>
            </div>
            <div className="md:w-1/2">
              <div className="bg-white rounded-lg shadow-xl p-2">
                <img 
                  src={heroImage}
                  alt="Biotech ecosystem" 
                  className="w-full h-auto rounded"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Recent Resources Section */}
      <RecentResourcesSection />
      
      {/* Participant types section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <div className="w-16 h-1 bg-primary-500 rounded mb-1 mx-auto"></div>
              <div className="w-10 h-1 bg-primary-500 rounded mx-auto"></div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Who We Connect</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our platform brings together the key players in the biotech and health tech industry
              to foster collaboration and accelerate innovation.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Startups */}
            <div className="bg-gray-50 rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Biotech Startups</h3>
              <p className="text-gray-600">
                Innovative companies developing new therapeutics, diagnostics, and medical technologies
                to address unmet healthcare needs.
              </p>
            </div>
            
            {/* Investors */}
            <div className="bg-gray-50 rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center text-green-600 mb-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Investors</h3>
              <p className="text-gray-600">
                Venture capital firms, angel investors, and strategic corporate investors looking for promising
                biotech and health tech opportunities.
              </p>
            </div>
            
            {/* Service Providers */}
            <div className="bg-gray-50 rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 mb-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Service Providers</h3>
              <p className="text-gray-600">
                CROs, CDMOs, regulatory consultants, legal experts, and other specialized service providers
                supporting the biotech and health tech industry.
              </p>
            </div>
            
            {/* Pharmaceutical Companies */}
            <div className="bg-gray-50 rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center text-red-600 mb-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Pharmaceutical Companies</h3>
              <p className="text-gray-600">
                Established pharmaceutical organizations seeking innovation through partnerships, licensing opportunities, and strategic collaborations with biotech startups.
              </p>
            </div>
          </div>
        </div><br></br>
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <div className="w-16 h-1 bg-primary-500 rounded mb-1 mx-auto"></div>
              <div className="w-10 h-1 bg-primary-500 rounded mx-auto"></div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Join Biotech01?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              <ul className="space-y-6 mt-8">
                <li>
                  <h3 className="text-lg font-bold text-gray-700">Showcase Your Innovation</h3>
                  <p className="text-gray-600">Easily upload your pitch deck and instantly become visible to investors and pharma companies seeking groundbreaking solutions.</p>
                </li>
                <li>
                  <h3 className="text-lg font-bold text-gray-700">Connect Privately and Securely</h3>
                  <p className="text-gray-600">Communicate directly within our app, safeguarding your privacy and eliminating unsolicited messages. Share your contact details only when you're ready.</p>
                </li>
                <li>
                  <h3 className="text-lg font-bold text-gray-700">Discover Essential Services</h3>
                  <p className="text-gray-600">Access a comprehensive network of expert service providers, including CROs, patent attorneys, API suppliers, consultants, and more.</p>
                </li>
                <li>
                  <h3 className="text-lg font-bold text-gray-700">Stay Informed</h3>
                  <p className="text-gray-600">Equip yourself with curated resources, guidelines, checklists, best practices, and the latest market statistics, empowering you to navigate the biotech and health tech landscape successfully.</p>
                </li>
              </ul>
            </p>
        </div>
      </section>
      
      {/* Features section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <div className="w-16 h-1 bg-primary-500 rounded mb-1 mx-auto"></div>
              <div className="w-10 h-1 bg-primary-500 rounded mx-auto"></div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Platform Features</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our comprehensive platform offers tools and connections for all stakeholders in the biotech ecosystem.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Feature 1: Pitch Deck Gallery */}
            <Link to="/gallery" className="group">
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 h-full transition-all duration-200 group-hover:shadow-md group-hover:border-primary-300">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 mb-4">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Pitch Deck Gallery</h3>
                <p className="text-gray-600">
                  View actual pitch decks prepared by startups that are ready to make deals and attract investment.
                </p>
              </div>
            </Link>
            
            {/* Feature 2: Participant Profiles */}
            <Link to="/profiles" className="group">
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 h-full transition-all duration-200 group-hover:shadow-md group-hover:border-primary-300">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 mb-4">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Participant Profiles</h3>
                <p className="text-gray-600">
                  Browse through profiles to find your ideal biotech partner, whether you're looking for investors, startups, or service providers.
                </p>
              </div>
            </Link>
            
            {/* Feature 3: Resources */}
            <Link to="/resources" className="group">
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 h-full transition-all duration-200 group-hover:shadow-md group-hover:border-primary-300">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 mb-4">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Learn through our Resources</h3>
                <p className="text-gray-600">
                  Access all the resources you need in one place, from patenting to clinical trials, to help grow your biotech venture.
                </p>
              </div>
            </Link>
            
            {/* Feature 4: Messaging */}
            <Link to="/messaging" className="group">
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 h-full transition-all duration-200 group-hover:shadow-md group-hover:border-primary-300">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 mb-4">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Messaging</h3>
                <p className="text-gray-600">
                  Contact your collaborators and partners securely in total privacy through our built-in messaging system.
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Success stories */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <div className="w-16 h-1 bg-primary-500 rounded mb-1 mx-auto"></div>
              <div className="w-10 h-1 bg-primary-500 rounded mx-auto"></div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Success Stories</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Discover how our platform has helped biotech and health tech companies find partners and accelerate growth.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Success story 1 */}
            <div className="bg-gray-50 rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full mr-4">
                  {/* Company logo would go here */}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">People Profilers</h3>
                  <p className="text-gray-600">Talent acquisition in life sciences</p>
                </div>
              </div>
              <p className="text-gray-700 mb-4">
                "Biotech01 is a fresh new community that gives me access to different types of talents and business that I was not able to get to, or even knew existed before. The platform is a great way to connect with the right people, and I've found more ways that I can bring value to this community, which also benefits myself and my business in the long run."
              </p>
              <p className="text-gray-600 font-medium">
                - Chirag Patel, Regional Consultant Manager - SE Asia & APAC
              </p>
            </div>
            
            {/* Success story 2 */}
            <div className="bg-gray-50 rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full mr-4">
                  {/* Company logo would go here */}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Ravis Technology</h3>
                  <p className="text-gray-600">EDC solutions for clinical research</p>
                </div>
              </div>
              <p className="text-gray-700 mb-4">
                "It's great to know that our services and know-how are needed and contributes to creating significant impact to lives. With the nature of Biotech01 as a community for the younger and newer type of innovators, what we do really matters and we get to "give" as well as do business. Pockets of innovations are happening all over the world, and in places not in the conventional sense. We get to support them towards success."
              </p>
              <p className="text-gray-600 font-medium">
                - Maleepan Phasupong, CEO & Founder
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="py-16 bg-primary-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Join the Biotech Ecosystem
          </h2>
          <p className="text-lg mb-8 max-w-3xl mx-auto">
            Whether you're a startup, investor, or service provider, become part of the growing
            network of biotech and health tech professionals.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            {isAuthenticated ? (
              <Link 
                to="/dashboard" 
                className="bg-white text-primary-700 hover:bg-gray-100 text-center py-3 px-6 rounded-lg text-lg"
              >
                Go to Dashboard
              </Link>
            ) : (
              <>
                <Link 
                  to="/signup" 
                  className="bg-white text-primary-700 hover:bg-gray-100 text-center py-3 px-6 rounded-lg text-lg"
                >
                  Create Account
                </Link>
                <Link 
                  to="/signin" 
                  className="border-2 border-white text-white hover:bg-primary-600 text-center py-3 px-6 rounded-lg text-lg"
                >
                  Sign In
                </Link>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 